import { Direction } from "./helpers.js";
import { Tile } from "./tile.js";
import { actorDB, Decision } from "./const.js";
import { buildCircuits, wirePretick, wireTick, } from "./wires.js";
import { msToProtoTime } from "./attemptTracker.js";
export var GameState;
(function (GameState) {
    GameState[GameState["PLAYING"] = 0] = "PLAYING";
    GameState[GameState["DEATH"] = 1] = "DEATH";
    GameState[GameState["TIMEOUT"] = 2] = "TIMEOUT";
    GameState[GameState["WON"] = 3] = "WON";
})(GameState || (GameState = {}));
export const onLevelStart = [];
export const onLevelDecisionTick = [];
export const onLevelWireTick = [];
export const onLevelAfterTick = [];
onLevelDecisionTick.push(level => {
    if (!level.selectedPlayable)
        return;
    if (level.subtick !== 2)
        return;
    if (level.selectedPlayable.cooldown > 0 ||
        level.selectedPlayable.slidingState ||
        level.selectedPlayable.playerBonked ||
        level.selectedPlayable.isPushing) {
        level.selectedPlayable.lastDecision = level.selectedPlayable.direction + 1;
    }
    else {
        level.selectedPlayable.lastDecision = Decision.NONE;
    }
});
/**
 * The state of a level, used as a hub of realtime level properties, the most important one being `field`
 */
export class LevelState {
    decisionTick(forcedOnly = false) {
        onLevelDecisionTick.forEach(val => val(this));
        for (let actor of Array.from(this.decidingActors)) {
            while (actor.newActor)
                actor = actor.newActor;
            if (!actor.exists)
                continue;
            actor._internalDecide(forcedOnly);
        }
    }
    moveTick() {
        for (let actor of Array.from(this.decidingActors)) {
            while (actor.newActor)
                actor = actor.newActor;
            if (!actor.exists)
                continue;
            actor._internalMove();
            actor._internalDoCooldown();
        }
    }
    getTime() {
        return `${this.currentTick}:${this.subtick} (${this.tickStage})`;
    }
    /**
     * Ticks the whole level by one subtick
     * (Since there are 3 subticks in a tick, and 20 ticks in a second, this should be run 60 times a second)
     */
    tick() {
        if (!this.levelStarted) {
            this.initializeLevel();
        }
        else {
            if (this.subtick === 2) {
                this.currentTick++;
                this.subtick = 0;
            }
            else
                this.subtick++;
        }
        if (this.timeLeft !== 0 && !this.timeFrozen) {
            this.timeLeft--;
            if (this.timeLeft <= 1)
                this.gameState = GameState.TIMEOUT;
        }
        if (this.inputProvider) {
            this.gameInput = this.inputProvider.getInput(this);
        }
        this.releasedKeys = {
            up: false,
            right: false,
            down: false,
            left: false,
            drop: false,
            rotateInv: false,
            switchPlayable: false,
        };
        wirePretick.apply(this);
        this.tickStage = "decision";
        this.decisionTick(this.subtick !== 2);
        this.tickStage = "move";
        this.moveTick();
        this.tickStage = "wire";
        onLevelWireTick.forEach(val => val(this));
        wireTick.apply(this);
        //	if (this.playables.length === 0) this.lost = true
        /*for (const debouncedKey of debouncedInputs)
            if (!this.gameInput[debouncedKey]) this.debouncedInputs[debouncedKey] = 0
            else if (this.debouncedInputs[debouncedKey] > 0) {
                if (this.debouncedInputs[debouncedKey] === 1)
                    this.debouncedInputs[debouncedKey]--
                this.debouncedInputs[debouncedKey]--
            } */
        if (this.gameState === GameState.TIMEOUT) {
            if (this.timeLeft > 1)
                this.gameState = GameState.PLAYING;
            else
                this.timeLeft -= 1;
        }
        if (this.playablesLeft <= 0) {
            if (this.gameState === GameState.PLAYING && this.timeLeft > 0) {
                this.timeLeft -= 1;
            }
            this.gameState = GameState.WON;
        }
        onLevelAfterTick.forEach(val => val(this));
    }
    /*
    debounceInput(inputType: typeof debouncedInputs[number]): void {
        if (this.debouncedInputs[inputType] !== -1)
            this.debouncedInputs[inputType] = debouncePeriod
    } */
    initializeLevel() {
        var _a, _b, _c;
        (_a = this.inputProvider) === null || _a === void 0 ? void 0 : _a.setupLevel(this);
        this.levelStarted = true;
        buildCircuits.apply(this);
        for (const actor of Array.from(this.actors)) {
            (_b = actor.levelStarted) === null || _b === void 0 ? void 0 : _b.call(actor);
            (_c = actor.onCreation) === null || _c === void 0 ? void 0 : _c.call(actor);
        }
        onLevelStart.forEach(val => val(this));
    }
    constructor(width, height) {
        this.width = width;
        this.height = height;
        this.playables = [];
        this.gameState = GameState.PLAYING;
        this.field = [];
        this.actors = [];
        this.decidingActors = [];
        this.subtick = 0;
        this.currentTick = 0;
        this.tickStage = "start";
        this.cameraType = { width: 10, height: 10, screens: 1 };
        this.hideWires = false;
        this.cc1Boots = false;
        this.chipsLeft = 0;
        this.chipsTotal = 0;
        this.chipsRequired = 0;
        this.timeLeft = 0;
        this.bonusPoints = 0;
        this.hintsLeft = [];
        this.hintsLeftInLevel = 0;
        this.playablesLeft = 0;
        this.playablesToSwap = false;
        this.levelStarted = false;
        this.createdN = 0;
        this.gameInput = {
            up: false,
            down: false,
            left: false,
            right: false,
            drop: false,
            rotateInv: false,
            switchPlayable: false,
        };
        this.releasedKeys = {
            up: false,
            right: false,
            down: false,
            left: false,
            drop: false,
            rotateInv: false,
            switchPlayable: false,
        };
        /**
         * Connections of 2 tiles, used for CC1-style clone machine and trap connections
         */
        this.connections = [];
        this.timeFrozen = false;
        this.resolvedCollisionCheckDirection = Direction.UP;
        this.prngValue1 = 0;
        this.prngValue2 = 0;
        this.blobPrngValue = 0x55;
        this.blob4PatternsMode = false;
        this.forcedPerspective = false;
        this.circuits = [];
        this.circuitInputs = [];
        this.circuitOutputs = [];
        this.circuitOutputStates = new Map();
        this.sfxManager = null;
        this.glitches = [];
        this.onGlitch = null;
        this.randomForceFloorDirection = Direction.UP;
        this.greenButtonPressed = false;
        this.blueButtonPressed = false;
        this.currentYellowButtonPress = 0;
        this.despawnedActors = [];
        //Init field
        this.field = [];
        for (let x = 0; x < width; x++) {
            this.field.push([]);
            for (let y = 0; y < height; y++)
                this.field[x].push(new Tile(this, [x, y], []));
        }
    }
    random() {
        let n = (this.prngValue1 >> 2) - this.prngValue1;
        if (!(this.prngValue1 & 0x02))
            n--;
        this.prngValue1 = ((this.prngValue1 >> 1) | (this.prngValue2 & 0x80)) & 0xff;
        this.prngValue2 = ((this.prngValue2 << 1) | (n & 0x01)) & 0xff;
        return this.prngValue1 ^ this.prngValue2;
    }
    blobMod() {
        let mod = this.blobPrngValue;
        if (this.blob4PatternsMode) {
            mod++;
            mod %= 4;
        }
        else {
            mod *= 2;
            if (mod < 255)
                mod ^= 0x1d;
            mod &= 0xff;
        }
        this.blobPrngValue = mod;
        return mod;
    }
    getHint() {
        if (!this.selectedPlayable)
            return null;
        const hintedActor = this.selectedPlayable.tile.findActor(actor => "hint" in actor && !!actor.hint);
        if (!hintedActor)
            return null;
        return hintedActor.hint;
    }
    getPerspective() {
        return (this.forcedPerspective ||
            (!!this.selectedPlayable &&
                this.selectedPlayable.hasTag("can-see-secrets")));
    }
    *tiles(rro = true, relativeTo = [0, 0]) {
        const stopAt = relativeTo[0] + relativeTo[1] * this.width;
        for (let pos = (stopAt + (rro ? this.width * this.height - 1 : +1)) %
            (this.width * this.height); pos !== stopAt; rro
            ? (pos =
                (pos + this.width * this.height - 1) % (this.width * this.height))
            : (pos = (pos + 1) % (this.width * this.height)))
            yield this.field[pos % this.width][Math.floor(pos / this.width)];
        yield this.field[relativeTo[0]][relativeTo[1]];
    }
    addGlitch(glitch) {
        var _a;
        const completeGlitch = {
            ...glitch,
            happensAt: msToProtoTime((this.currentTick * 3 + this.subtick) * (1000 / 60)),
        };
        this.glitches.push(completeGlitch);
        (_a = this.onGlitch) === null || _a === void 0 ? void 0 : _a.call(this, completeGlitch);
    }
}
export function createLevelFromData(data) {
    const level = new LevelState(data.width, data.height);
    level.levelData = data;
    if (data.hints)
        level.hintsLeft = [...data.hints];
    if (data.defaultHint)
        level.defaultHint = data.defaultHint;
    // TODO Misc data setting, like CC1 boots and stuff
    if (data.blobMode) {
        if (data.blobMode > 1)
            level.blobPrngValue = Math.floor(Math.random() * 0x100);
        level.blob4PatternsMode = data.blobMode === 4;
    }
    level.cameraType = data.camera;
    level.timeLeft = Math.max(0, data.timeLimit * 60);
    if (data.playablesRequiredToExit !== "all")
        level.playablesLeft = data.playablesRequiredToExit;
    level.hideWires = !!data.hideWires;
    level.cc1Boots = !!data.cc1Boots;
    if (data.extraChipsRequired)
        level.chipsRequired = data.extraChipsRequired;
    if (data.connections)
        level.connections = data.connections;
    for (let y = 0; y < level.height; y++)
        for (let x = 0; x < level.width; x++)
            for (const actor of data.field[x][y]) {
                if (!actor[0]) {
                    if (actor[3]) {
                        const tile = level.field[x][y];
                        tile.wires = actor[3] & 0x0f;
                        tile.wireTunnels = (actor[3] & 0xf0) >> 4;
                    }
                    continue;
                }
                if (!actorDB[actor[0]])
                    throw new Error(`Cannot find actor with id "${actor[0]}"!`);
                const actorInstance = new actorDB[actor[0]](level, [x, y], actor[2], actor[1]);
                if (actor[3]) {
                    actorInstance.wires = actor[3] & 0x0f;
                    actorInstance.wireTunnels = actor[3] & 0xf0;
                }
            }
    return level;
}
//# sourceMappingURL=level.js.map